import React, { useEffect, useMemo, useState } from 'react';
import { safeArray } from '../../components/ErrorBoundary';
import TabbedSection from '../../components/TabbedSection';
import StatusBadge from '../../components/StatusBadge';
import { submittalsAPI } from '../../services/authService';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifyError, notifySuccess } from '../../utils/notify';

const FIELD_TYPES = ['TEXT', 'TEXTAREA', 'NUMBER', 'DATE', 'SELECT', 'MULTI_SELECT', 'BOOLEAN', 'FILE', 'USER', 'PARTY', 'CHECKLIST'];

const emptyFieldForm = () => ({
  field_key: '',
  label: '',
  field_type: 'TEXT',
  is_required: false,
  display_order: 0,
  help_text: '',
  options_text: '',
});

const optionsToText = (options) => {
  if (!options) return '';
  if (Array.isArray(options)) return options.join('\n');
  return '';
};

const textToOptions = (text) => {
  const items = String(text || '')
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
  return items.length ? items : null;
};

const SubmittalTemplatesAdmin = () => {
  const [activeTab, setActiveTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [fields, setFields] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [categoryForm, setCategoryForm] = useState({ name: '', code: '', description: '', default_review_days: 7 });
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', is_default: false });
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [fieldForm, setFieldForm] = useState(emptyFieldForm());
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [overwriteSampleFields, setOverwriteSampleFields] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadCategories = async () => {
    const resp = await submittalsAPI.getCategories();
    setCategories(safeArray(resp?.data?.data));
  };

  const loadTemplates = async (categoryId) => {
    const params = categoryId ? { categoryId } : {};
    const resp = await submittalsAPI.getTemplates(params);
    setTemplates(safeArray(resp?.data?.data));
  };

  const loadFields = async (templateId) => {
    if (!templateId) { setFields([]); return; }
    const resp = await submittalsAPI.getTemplateFields(templateId);
    setFields(safeArray(resp?.data?.data));
  };

  useEffect(() => {
    loadCategories().catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    loadTemplates(selectedCategoryId || undefined).catch(() => setTemplates([]));
  }, [selectedCategoryId]);

  useEffect(() => {
    loadFields(selectedTemplateId).catch(() => setFields([]));
  }, [selectedTemplateId]);

  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [fields],
  );

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(selectedCategoryId)),
    [categories, selectedCategoryId],
  );

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submittalsAPI.createCategory({
        name: categoryForm.name,
        code: categoryForm.code,
        description: categoryForm.description,
        default_review_days: Number(categoryForm.default_review_days) || null,
      });
      notifySuccess('Category created.');
      setCategoryForm({ name: '', code: '', description: '', default_review_days: 7 });
      await loadCategories();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to create category.');
    } finally {
      setLoading(false);
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({ name: '', description: '', is_default: false });
    setEditingTemplateId(null);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!selectedCategoryId) {
      notifyError('Select a category first.');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        category_id: parseInt(selectedCategoryId, 10),
        name: templateForm.name,
        description: templateForm.description,
        is_default: templateForm.is_default,
      };
      if (editingTemplateId) {
        await submittalsAPI.updateTemplate(editingTemplateId, payload);
        notifySuccess('Template updated.');
      } else {
        await submittalsAPI.createTemplate(payload);
        notifySuccess('Template created.');
      }
      resetTemplateForm();
      await loadTemplates(selectedCategoryId);
      await loadCategories();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to save template.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplateId(template.id);
    setTemplateForm({
      name: template.name || '',
      description: template.description || '',
      is_default: !!(template.isDefault ?? template.is_default),
    });
  };

  const resetFieldForm = () => {
    setFieldForm(emptyFieldForm());
    setEditingFieldId(null);
  };

  const buildFieldPayload = () => {
    const payload = {
      field_key: fieldForm.field_key.trim(),
      label: fieldForm.label.trim(),
      field_type: fieldForm.field_type,
      is_required: fieldForm.is_required,
      display_order: Number(fieldForm.display_order) || 0,
      help_text: fieldForm.help_text || null,
    };
    if (['SELECT', 'MULTI_SELECT'].includes(fieldForm.field_type)) {
      payload.options = textToOptions(fieldForm.options_text);
    }
    return payload;
  };

  const handleSaveField = async (e) => {
    e.preventDefault();
    if (!selectedTemplateId) {
      notifyError('Select a template first.');
      return;
    }
    setLoading(true);
    try {
      const payload = buildFieldPayload();
      if (editingFieldId) {
        await submittalsAPI.updateTemplateField(editingFieldId, payload);
        notifySuccess('Field updated.');
      } else {
        await submittalsAPI.createTemplateField(selectedTemplateId, payload);
        notifySuccess('Field created.');
      }
      resetFieldForm();
      await loadFields(selectedTemplateId);
      await loadCategories();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to save field.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditField = (field) => {
    setEditingFieldId(field.id);
    setFieldForm({
      field_key: field.field_key || '',
      label: field.label || '',
      field_type: field.field_type || 'TEXT',
      is_required: !!field.is_required,
      display_order: field.display_order ?? 0,
      help_text: field.help_text || '',
      options_text: optionsToText(field.options),
    });
  };

  const handleDeleteField = async (field) => {
    if (field.is_required) {
      notifyError('Required template fields cannot be deleted.');
      return;
    }
    if (!(await confirmDialog('Delete this template field?', 'Delete Field'))) return;
    try {
      await submittalsAPI.deleteTemplateField(field.id);
      notifySuccess('Field deleted.');
      if (editingFieldId === field.id) resetFieldForm();
      await loadFields(selectedTemplateId);
      await loadCategories();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to delete field.');
    }
  };

  const handleManageFields = (category) => {
    const defaultTemplate = category.default_template
      || category.templates?.find((t) => t.isDefault || t.is_default)
      || category.templates?.[0];
    setSelectedCategoryId(String(category.id));
    if (defaultTemplate?.id) setSelectedTemplateId(String(defaultTemplate.id));
    setActiveTab('fields');
  };

  const handleLoadSampleFields = async () => {
    if (!selectedTemplateId) {
      notifyError('Select a template first.');
      return;
    }
    const msg = overwriteSampleFields
      ? 'This will reset existing sample fields to default values. Continue?'
      : 'This will add missing default sample fields without changing existing fields. Continue?';
    if (!(await confirmDialog(msg, 'Load Default Sample Fields'))) return;
    setLoading(true);
    try {
      const resp = await submittalsAPI.loadTemplateSampleFields(selectedTemplateId, {
        overwrite_existing: overwriteSampleFields,
      });
      const data = resp?.data || {};
      notifySuccess(`Sample fields loaded: ${data.fieldsCreated || 0} added, ${data.fieldsUpdated || 0} updated, ${data.fieldsSkipped || 0} unchanged.`);
      await loadFields(selectedTemplateId);
      await loadCategories();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to load sample fields.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Submittal Templates Admin</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>Manage submittal categories, templates, and dynamic fields.</p>
        </div>
      </div>

      <TabbedSection
        activeKey={activeTab}
        onChange={setActiveTab}
        tabs={[
          {
            key: 'categories',
            label: 'Categories',
            content: (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 20 }}>
                <form onSubmit={handleCreateCategory} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                  <h3 style={{ marginTop: 0 }}>Add Category</h3>
                  <div className="form-group"><label>Name</label><input className="form-input" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} required /></div>
                  <div className="form-group"><label>Code</label><input className="form-input" value={categoryForm.code} onChange={(e) => setCategoryForm({ ...categoryForm, code: e.target.value })} required /></div>
                  <div className="form-group"><label>Description</label><input className="form-input" value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} /></div>
                  <div className="form-group"><label>Default Review Days</label><input type="number" className="form-input" value={categoryForm.default_review_days} onChange={(e) => setCategoryForm({ ...categoryForm, default_review_days: e.target.value })} /></div>
                  <button type="submit" className="btn-primary btn-sm" disabled={loading}>Create Category</button>
                </form>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Code</th>
                        <th>Review Days</th>
                        <th>Default Template</th>
                        <th>Field Count</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((c) => (
                        <tr key={c.id}>
                          <td>{c.name}</td>
                          <td>{c.code}</td>
                          <td>{c.default_review_days ?? '—'}</td>
                          <td>{c.default_template?.name || '—'}</td>
                          <td>{c.default_field_count ?? 0}</td>
                          <td>
                            <button type="button" className="btn-secondary btn-sm" onClick={() => handleManageFields(c)}>
                              Manage Fields
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ),
          },
          {
            key: 'templates',
            label: 'Templates',
            content: (
              <div>
                <div className="filters-row" style={{ marginBottom: 12 }}>
                  <select className="filter-select" value={selectedCategoryId} onChange={(e) => { setSelectedCategoryId(e.target.value); resetTemplateForm(); }}>
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 20 }}>
                  <form onSubmit={handleSaveTemplate} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                    <h3 style={{ marginTop: 0 }}>{editingTemplateId ? 'Edit Template' : 'Add Template'}</h3>
                    <div className="form-group"><label>Name</label><input className="form-input" value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} required /></div>
                    <div className="form-group"><label>Description</label><input className="form-input" value={templateForm.description} onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })} /></div>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                      <input type="checkbox" checked={templateForm.is_default} onChange={(e) => setTemplateForm({ ...templateForm, is_default: e.target.checked })} />
                      Default template
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="submit" className="btn-primary btn-sm" disabled={loading}>{editingTemplateId ? 'Update Template' : 'Create Template'}</button>
                      {editingTemplateId && (
                        <button type="button" className="btn-secondary btn-sm" onClick={resetTemplateForm}>Cancel</button>
                      )}
                    </div>
                  </form>
                  <div className="table-container">
                    <table className="data-table">
                      <thead><tr><th>Name</th><th>Category</th><th>Default</th><th>Actions</th></tr></thead>
                      <tbody>
                        {templates.map((t) => (
                          <tr key={t.id}>
                            <td>{t.name}</td>
                            <td>{t.category?.name || t.categoryId}</td>
                            <td>{(t.isDefault ?? t.is_default) ? <StatusBadge tone="approved">Default</StatusBadge> : 'No'}</td>
                            <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button type="button" className="btn-secondary btn-sm" onClick={() => handleEditTemplate(t)}>Edit</button>
                              <button type="button" className="btn-secondary btn-sm" onClick={() => { setSelectedTemplateId(String(t.id)); setActiveTab('fields'); }}>Fields</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ),
          },
          {
            key: 'fields',
            label: 'Template Fields',
            content: (
              <div>
                <div className="filters-row" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
                  <select className="filter-select" value={selectedCategoryId} onChange={(e) => { setSelectedCategoryId(e.target.value); setSelectedTemplateId(''); resetFieldForm(); }}>
                    <option value="">Select category</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select className="filter-select" value={selectedTemplateId} onChange={(e) => { setSelectedTemplateId(e.target.value); resetFieldForm(); }}>
                    <option value="">Select template</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{(t.isDefault ?? t.is_default) ? ' (Default)' : ''}</option>)}
                  </select>
                  <button type="button" className="btn-secondary btn-sm" disabled={!selectedTemplateId || loading} onClick={handleLoadSampleFields}>
                    Load Default Sample Fields
                  </button>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                    <input type="checkbox" checked={overwriteSampleFields} onChange={(e) => setOverwriteSampleFields(e.target.checked)} />
                    Overwrite existing sample fields
                  </label>
                </div>
                {selectedCategory && (
                  <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
                    Category: <strong>{selectedCategory.name}</strong>
                    {selectedTemplateId ? ` · Template #${selectedTemplateId}` : ''}
                  </p>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 20 }}>
                  <form onSubmit={handleSaveField} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                    <h3 style={{ marginTop: 0 }}>{editingFieldId ? 'Edit Field' : 'Add Field'}</h3>
                    <div className="form-group"><label>Field Key</label><input className="form-input" value={fieldForm.field_key} onChange={(e) => setFieldForm({ ...fieldForm, field_key: e.target.value })} required disabled={!!editingFieldId} /></div>
                    <div className="form-group"><label>Label</label><input className="form-input" value={fieldForm.label} onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })} required /></div>
                    <div className="form-group">
                      <label>Type</label>
                      <select className="form-input" value={fieldForm.field_type} onChange={(e) => setFieldForm({ ...fieldForm, field_type: e.target.value })}>
                        {FIELD_TYPES.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
                      </select>
                    </div>
                    {['SELECT', 'MULTI_SELECT'].includes(fieldForm.field_type) && (
                      <div className="form-group">
                        <label>Options (one per line or comma-separated)</label>
                        <textarea className="form-input" rows={4} value={fieldForm.options_text} onChange={(e) => setFieldForm({ ...fieldForm, options_text: e.target.value })} />
                      </div>
                    )}
                    <div className="form-group"><label>Display Order</label><input type="number" className="form-input" value={fieldForm.display_order} onChange={(e) => setFieldForm({ ...fieldForm, display_order: Number(e.target.value) })} /></div>
                    <div className="form-group"><label>Help Text</label><input className="form-input" value={fieldForm.help_text} onChange={(e) => setFieldForm({ ...fieldForm, help_text: e.target.value })} /></div>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                      <input type="checkbox" checked={fieldForm.is_required} onChange={(e) => setFieldForm({ ...fieldForm, is_required: e.target.checked })} />
                      Required
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="submit" className="btn-primary btn-sm" disabled={loading}>{editingFieldId ? 'Update Field' : 'Create Field'}</button>
                      {editingFieldId && <button type="button" className="btn-secondary btn-sm" onClick={resetFieldForm}>Cancel</button>}
                    </div>
                  </form>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Key</th>
                          <th>Label</th>
                          <th>Type</th>
                          <th>Required</th>
                          <th>Options</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedFields.length === 0 ? (
                          <tr><td colSpan={7} style={{ textAlign: 'center', color: '#6b7280' }}>No fields configured</td></tr>
                        ) : sortedFields.map((f) => (
                          <tr key={f.id}>
                            <td>{f.display_order ?? 0}</td>
                            <td>{f.field_key}</td>
                            <td>{f.label}</td>
                            <td>{f.field_type}</td>
                            <td>{f.is_required ? <StatusBadge tone="high">Required</StatusBadge> : 'No'}</td>
                            <td style={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word', fontSize: 12 }}>
                              {Array.isArray(f.options) ? f.options.join(', ') : '—'}
                            </td>
                            <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <button type="button" className="btn-secondary btn-sm" onClick={() => handleEditField(f)}>Edit</button>
                              <button type="button" className="btn-secondary btn-sm" disabled={f.is_required} onClick={() => handleDeleteField(f)}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default SubmittalTemplatesAdmin;
